import { DOCUMENT } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ShepherdService } from 'angular-shepherd';

import { SessionTimeoutService } from '@core/services/session-timeout.service';
import { LayoutService } from '@layout/layout.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { SupportChatWindowDesktopDrawerService } from '@shared/support-chat-window/support-chat-window-desktop-drawer/support-chat-window-desktop-drawer.service';

@Injectable({
  providedIn: 'root',
})
export class AppOverlayCleanupService {
  private readonly documentRef = inject(DOCUMENT);
  private readonly dialog = inject(MatDialog);
  private readonly injector = inject(Injector);
  private readonly layoutService = inject(LayoutService);
  private readonly matSnackBar = inject(MatSnackBar);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly nzModalService = inject(NzModalService);
  private readonly nzNotificationService = inject(NzNotificationService);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly shepherdService = inject(ShepherdService);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly supportChatWindowDesktopDrawerService = inject(SupportChatWindowDesktopDrawerService);

  public closeAll(): void {
    [
      () => this.injector.get(SessionTimeoutService).stopIdleWatch(),
      () => this.layoutService.closeInfoBar(),
      () => this.shepherdService.cancel(),
      () => this.supportChatWindowDesktopDrawerService.close(),
      () => this.stackedMessages.closeAll(),
      () => this.matSnackBar.dismiss(),
      () => this.dialog.closeAll(),
      () => this.nzModalService.closeAll(),
      () => this.nzMessageService.remove(),
      () => this.nzNotificationService.remove(),
    ].forEach(cleanup => this.runCleanup(cleanup));

    this.forceCloseFloatingElements();
  }

  private runCleanup(cleanup: () => void): void {
    try {
      cleanup();
    } catch {
      // Cleanup must continue even if one overlay owner is already destroyed.
    }
  }

  private forceCloseFloatingElements(): void {
    this.hideOpenPopoverOverlays();
    this.removeGlobalFloatingElements();
    this.clearOverlayContainer();
    this.clearGlobalOverlayState();
  }

  private hideOpenPopoverOverlays(): void {
    this.documentRef.querySelectorAll<HTMLElement>('[popover]:popover-open').forEach(element => {
      if (typeof element.hidePopover === 'function') {
        this.runCleanup(() => element.hidePopover());
      }
    });
  }

  private removeGlobalFloatingElements(): void {
    const selectors = [
      '.ant-dropdown',
      '.ant-message',
      '.ant-notification',
      '.ant-picker-dropdown',
      '.ant-popover',
      '.ant-select-dropdown',
      '.ant-tooltip',
      '.bridge-stacked-messages-host',
      '.ng-dropdown-panel',
      '.shepherd-element',
      '.shepherd-modal-overlay-container',
    ];

    this.documentRef.querySelectorAll(selectors.join(',')).forEach(element => {
      element.remove();
    });
  }

  private clearOverlayContainer(): void {
    Array.from(this.overlayContainer.getContainerElement().children).forEach(element => {
      element.remove();
    });
  }

  private clearGlobalOverlayState(): void {
    this.documentRef.body?.classList.remove(
      'ant-scrolling-effect',
      'bridge-stacked-messages-active',
      'cdk-global-scrollblock'
    );
    this.documentRef.documentElement?.classList.remove('cdk-global-scrollblock');
    this.documentRef.body?.style.removeProperty('overflow');
    this.documentRef.body?.style.removeProperty('width');
  }
}
